import { debounce } from '@sama/micro-dash';
import { Subject } from 'rxjs';
import {
  EventTrackingOptions,
  EventTrackingValue,
} from '../../team-apis/core/event-tracking';
import { AbstractTrackerWithContext } from './abstract-tracker-with-context';
import { EventBatchValues } from './event-batch-values';
import { Tracker, TrackingEvent } from './event-utils';

export const pendingDebounces = new Set<{ cancel(): void }>();

export class DebouncedEventTracker extends AbstractTrackerWithContext {
  afterDebounce$ = new Subject<void>();
  private debounce = debounce(this.afterDebounce.bind(this), 1000);
  private batchValues?: EventBatchValues;

  constructor(
    private eventWithoutContext: TrackingEvent,
    parentTracker: Tracker,
    // Callers can pass `shellConnector.pushAndTrack` to push to the
    // undo stack once, at the end of the debounce window
    private finalEventTrackingFn?: Tracker['track'],
  ) {
    super(parentTracker);
  }

  /**
   * Adds context that will be cleared after each debounce succeeds. It will
   * take precedence over the normal context of this tracker.
   */
  addBatchContext(context: EventTrackingValue) {
    this.ensureBatch();
    this.batchValues!.addContext(context);
  }

  startEvent() {
    this.ensureBatch();
    this.batchValues!.startEvent();
  }

  trackWithDebounce(event: EventTrackingValue, options?: EventTrackingOptions) {
    this.ensureBatch();
    this.batchValues!.endEvent();
    this.debounce(
      this.mergeContext({ ...this.eventWithoutContext, ...event }),
      options,
    );
    pendingDebounces.add(this.debounce);
  }

  /** @deprecated */
  override track() {
    throw new Error('use trackWithDebounce()');
  }

  isDebouncePending() {
    return pendingDebounces.has(this.debounce);
  }

  protected override mergeContext(event: TrackingEvent) {
    return super.mergeContext({
      ...this.batchValues!.buildEventTrackingValue(),
      ...event,
    });
  }

  private ensureBatch() {
    if (!this.batchValues) {
      this.batchValues = new EventBatchValues(
        { ...this.eventWithoutContext, ...this.context },
        this.parentTracker,
      );
    }
  }

  private afterDebounce(event: TrackingEvent, options?: EventTrackingOptions) {
    if (!this.batchValues) {
      throw new Error('a debounce that was never started?');
    }

    if (this.finalEventTrackingFn) {
      this.finalEventTrackingFn(event, options);
    } else {
      this.trackWithoutContext(event, options);
    }
    this.batchValues = undefined;
    pendingDebounces.delete(this.debounce);
    this.afterDebounce$.next();
  }
}
